import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeQuery } from '../../graphql';
import { getAllMiscVariants } from '../../graphql/queries';
import Wizard from '../../components/Wizard';
import Tooltip from '../../components/Tooltip';
import { usePageContent } from '../../hooks/usePageContent';
import { trackGiftAddToCart } from '../../utils/analytics';
import './index.module.css';

const GIFT_THEMES = [
  {
    id: 'Gift_Card_Generic',
    displayName: 'Generic',
    iconSrc: '/images/content/Gift_Generic.svg',
    previewSrc: '/images/content/gift-card-preview-Gift_Generic.svg',
  },
  {
    id: 'Gift_Card_Birthday',
    displayName: 'Birthday',
    iconSrc: '/images/content/Gift_Birthday.svg',
    previewSrc: '/images/content/gift-card-preview-Gift_Birthday.svg',
  },
  {
    id: 'Gift_Card_Christmas',
    displayName: 'Christmas',
    iconSrc: '/images/content/Gift_Christmas.svg',
    previewSrc: '/images/content/gift-card-preview-Gift_Christmas.svg',
  },
];

const namePattern = /^[a-zA-Z\-–—'\s]+$/;

const GiftCardPersonalisePage = () => {
  const navigate = useNavigate();
  const { find } = usePageContent('gift-personalise');
  const wizardContent = find('wizard') || {};
  const c = find('personaliseForm') || {};
  const wizardSteps = wizardContent.steps || [
    { title: 'Personalise voucher', href: '/gift-vouchers/personalise-voucher' },
    { title: 'Confirm delivery details', href: '/gift-vouchers/confirm-delivery' },
    { title: 'Checkout', href: '/gift-vouchers/payment' },
  ];
  const fe = c.fieldErrors || {};
  const messageMaxChars = c.messageMaxChars || 250;

  const [selectedThemeId, setSelectedThemeId] = useState(GIFT_THEMES[0].id);
  const [voucherTo, setVoucherTo] = useState('');
  const [voucherFrom, setVoucherFrom] = useState('');
  const [voucherMessage, setVoucherMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [giftVariants, setGiftVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantsLoading, setVariantsLoading] = useState(true);

  useEffect(() => {
    executeQuery(getAllMiscVariants)
      .then(data => {
        const variants = (data?.getAllMiscVariants || [])
          .filter(v => v.DisplayValue?.startsWith('GV '));
        setGiftVariants(variants);
        if (variants.length > 0) setSelectedVariant(variants[0]);
      })
      .catch(() => {})
      .finally(() => setVariantsLoading(false));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (voucherTo && !namePattern.test(voucherTo)) newErrors.voucherTo = fe.voucherTo || 'Please enter a valid name';
    if (voucherFrom && !namePattern.test(voucherFrom)) newErrors.voucherFrom = fe.voucherFrom || 'Please enter a valid name';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const theme = GIFT_THEMES.find(t => t.id === selectedThemeId);
    const giftOrderData = {
      themeId: selectedThemeId,
      themeDisplayName: theme?.displayName || '',
      themePreviewSrc: theme?.previewSrc || '',
      to: voucherTo,
      from: voucherFrom,
      message: voucherMessage,
      amount: 0,
      // Numeric misc variant Id — used by getMiscVariantById (Int!) and createOrder VariantId. Keep numeric.
      variantId: selectedVariant?.Id || 0,
      // Voucher face value from "GV 195" -> "195"; used for the GA item_category2 (themeId_amount).
      voucherAmount: selectedVariant?.DisplayValue?.replace(/^GV\s*/i, '').trim() || '',
    };
    // GA: add_to_cart once the voucher is personalised (prod: giftPersonalisation.js).
    trackGiftAddToCart(giftOrderData);
    sessionStorage.setItem('giftOrderData', JSON.stringify(giftOrderData));
    navigate('/gift-vouchers/confirm-delivery');
  };

  const selectedTheme = GIFT_THEMES.find(t => t.id === selectedThemeId);

  return (
    <div id="content">
      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Buy a gift voucher'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <Wizard
            steps={wizardSteps}
            currentStep={0}
            backLink="/gift-vouchers"
            backLabel={c.backLabel || 'Back to gift voucher landing'}
          />

          <div className="plate-wizard__form plate-wizard__form--gift">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">{c.sectionTitle || 'Personalise your gift voucher'}</h2>
            </div>

            <form className="gift-personalise form" onSubmit={handleSubmit} noValidate>
              <div className="gift-personalise__form-content gift-personalise__form-content--ready">

                <div className="gift-personalise__theme">
                  <fieldset className="gift-personalise__graphic-radio">
                    <legend className="input__label hidden">{c.themeLabel || 'Select a voucher theme'}</legend>
                    <div className="gift-personalise__graphic-radio-options hidden">
                      {GIFT_THEMES.map(theme => (
                        <div key={theme.id} className="gift-personalise__graphic-radio-option">
                          <input
                            className="gift-personalise__graphic-radio-option-input visuallyhidden visuallyhidden--no-focus"
                            type="radio"
                            id={`voucher-theme-${theme.id}`}
                            value={theme.id}
                            name="voucher-theme"
                            checked={selectedThemeId === theme.id}
                            onChange={() => setSelectedThemeId(theme.id)}
                          />
                          <label
                            className="gift-personalise__graphic-radio-option-label"
                            htmlFor={`voucher-theme-${theme.id}`}
                            style={{ backgroundImage: `url(${theme.iconSrc})` }}
                          >
                            <span className="gift-personalise__graphic-radio-option-label-text">
                              {theme.displayName}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  {selectedTheme && (
                    <div className="gift-personalise__gift-card-previews">
                      <div className="gift-personalise__gift-card-preview">
                        <img
                          src={selectedTheme.previewSrc}
                          alt={`${selectedTheme.displayName} voucher preview`}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="gift-personalise__message">
                  <div className={`input${errors.voucherTo ? ' input--error' : ''}`}>
                    <div className="input__header">
                      <label className="input__label" htmlFor="voucher-to">{c.toLabel || 'To (optional)'}</label>
                    </div>
                    <input
                      className="input__ctrl"
                      type="text"
                      id="voucher-to"
                      name="voucher-to"
                      maxLength={60}
                      placeholder={c.toPlaceholder || 'E.g. Mary'}
                      value={voucherTo}
                      onChange={e => { setVoucherTo(e.target.value); setErrors(prev => ({ ...prev, voucherTo: '' })); }}
                    />
                    {errors.voucherTo && (
                      <div className="input__error-container">
                        <div className="input__error"><p>{errors.voucherTo}</p></div>
                      </div>
                    )}
                  </div>



                  <div className="input">
                    <div className="input__header">
                      <label className="input__label" htmlFor="voucher-message">
                        {c.messageLabel || 'Include a message (optional)'}
                      </label>
                    </div>
                    <p className="input__sublabel">
                      {c.messageSublabel || 'This message will be included in the gift voucher sent to the recipient.'}
                    </p>
                    <textarea
                      className="input__ctrl input__ctrl--textarea input__ctrl--max-510"
                      id="voucher-message"
                      name="voucher-message"
                      maxLength={messageMaxChars}
                      placeholder={c.messagePlaceholder || 'Enter your message here...'}
                      value={voucherMessage}
                      onChange={e => setVoucherMessage(e.target.value)}
                      rows={4}
                    />
                  </div>
                  
                  <p className="gift-personalise__counter">
                    {messageMaxChars - voucherMessage.length} {c.charsLeftText || 'characters left.'}
                  </p>


                  <div className={`input${errors.voucherFrom ? ' input--error' : ''}`}>
                    <div className="input__header">
                      <label className="input__label" htmlFor="voucher-from">{c.fromLabel || 'From (optional)'}</label>
                    </div>
                    <input
                      className="input__ctrl"
                      type="text"
                      id="voucher-from"
                      name="voucher-from"
                      maxLength={60}
                      placeholder={c.fromPlaceholder || 'E.g. Jane'}
                      value={voucherFrom}
                      onChange={e => { setVoucherFrom(e.target.value); setErrors(prev => ({ ...prev, voucherFrom: '' })); }}
                    />
                    {errors.voucherFrom && (
                      <div className="input__error-container">
                        <div className="input__error"><p>{errors.voucherFrom}</p></div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="gift-personalise__variants">
                  <div className="input">
                    <label className="input__label" htmlFor="voucher-amount">{c.amountLabel || 'Select an amount'}</label>
                    {variantsLoading ? (
                      <p>{c.loadingAmountsText || 'Loading amounts...'}</p>
                    ) : (
                      <select
                        className="input__ctrl input__ctrl--select input__ctrl--fixed-100"
                        id="voucher-amount"
                        name="voucher-amount"
                        value={selectedVariant?.Id || ''}
                        onChange={e => {
                          const v = giftVariants.find(v => v.Id === Number(e.target.value));
                          setSelectedVariant(v || null);
                        }}
                      >
                        {giftVariants.map(v => (
                          <option key={v.Id} value={v.Id}>${v.DisplayValue.replace(/^GV\s*/i, '')}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="gift-personalise__variants-details">
                    {(() => {
                      const variantAmount = selectedVariant?.DisplayValue?.replace(/^GV\s*/i, '').trim();
                      const related = variantAmount ? c.relatedProductsByVariant?.[variantAmount] : null;
                      if (!related?.products?.length) return null;
                      return (
                        <div className="gift-personalise__related">
                          {related.text && (
                            <p className="gift-personalise__related-text">{related.text}</p> 
                          )}
                          <br />
                          <ul className="gift-personalise__related-products">
                            {related.products.map((product, i) => (
                              <li key={i} className="gift-personalise__related-product">
                                <img
                                  src={product.imagePath}
                                  alt={product.name}
                                  className="gift-personalise__related-product-image"
                                />
                                <span className="gift-personalise__related-product-name">{product.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                </div>

              </div>

              <div className="gift-personalise__actions gift-personalise__actions--ready">
                <button type="submit" className="button button--full-width">
                  <span className="button__text">{c.continueText || 'Continue'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardPersonalisePage;
